import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogTitle,
} from "@mui/material";
import { RxStoreContext } from "../../../stores/Rx/rx.provider";
import useStore from "../../../helpers/useStore";
import {
  AddressDto,
  AddressDtoTypeEnum,
  GiftCertificateDto,
  SalesTemplateDto,
  TherapyDetailsDto,
} from "../../../api-client";
import { AppointmentContext } from "../../../stores/Appointment/appointment.provider";
import { PatientsContext } from "../../../stores/Patients/patients.provider";
import { UserContext } from "../../../stores/User/User.provider";
import { useCurrentLocation } from "../../../stores/User/user.queries";
import { SalesStoreContext } from "../../../stores/Sales/sales.provider";
import GCBill from "./GCTable";
import notificationStore from "../../../stores/Notification/notificationStore";
import FlatCard from "../../../atoms/FlatCard";
import GenericTable from "../SelectableTable";
import GCTable from "./GCTable";
import SelectableTable from "../SelectableTable";
import Controls from "../../../atoms/Controls";

const SalesPageMain = observer(() => {
  const SalesStore = useStore(SalesStoreContext);
  const { getSaleServices, createGiftCertificate, getUnpaidGiftCertificates } = SalesStore;
  const UserStore = useStore(UserContext);
  const { user } = UserStore;
  const currentLocation = useCurrentLocation();

  const [saleServices, setSaleServices] = useState<TherapyDetailsDto[]>([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [salesTemplate, setSalesTemplate] = useState<SalesTemplateDto>({
    therapies: [],
    amount: undefined,
    addressId: "",
  });
  const [totalPrice, setTotalPrice] = useState(0);
  const [manualPriceEdit, setManualPriceEdit] = useState(false);
  const [giftCertificatesPayload, setGiftCertificatesPayload] = useState<GiftCertificateDto>({
    value: 0,
  });
  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [accessUrl, setAccessUrl] = useState<string>();
  const [dataRefreshFlag, setDataRefreshFlag] = useState(false);
  const [addresses, setAddresses] = useState<AddressDto[]>([]);
  const [unpaidGiftCertificates, setUnpaidGiftCertificates] = useState<GiftCertificateDto[]>([]);
  const [gcDisplay, setGCDisplay] = useState<GiftCertificateDto | null>(null);

  const [selectedAddressType, setSelectedAddressType] = useState<AddressDtoTypeEnum | undefined>();
  const [visitFee, setVisitFee] = useState<string | undefined>();
  const [hbPrice, setHbPrice] = useState<number>(0);

  const calculateTotalPrice = () => {
    const selectedTherapies = saleServices.filter((service) =>
      selectedServiceIds.includes(service.id!),
    );

    const total = selectedTherapies.reduce((sum, therapy: any) => {
      const therapyPrice = Number(therapy.price);
      return sum + (isNaN(therapyPrice) ? 0 : therapyPrice);
    }, 0);

    const amount = salesTemplate.amount != null ? salesTemplate.amount : 1;
    // Add visit fee price if HB is selected
    const visitFeeTotal = visitFee === "HB" ? hbPrice : 0;
    return parseFloat((total * amount + visitFeeTotal)?.toFixed(2));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getSaleServices();
        const hbService = data.find((service: any) => service.abbreviation === "HB");
        setHbPrice(hbService ? hbService.price : 0);
        const filteredData = data.filter((service: any) => service.duration !== null);
        setSaleServices(filteredData);
      } catch (error) {
        notificationStore.showMessage("Fehler beim Laden der Leistungen", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchUnpaidCertificates = async () => {
      const data = await getUnpaidGiftCertificates();
      setUnpaidGiftCertificates(data);
    };
    fetchUnpaidCertificates();
  }, [getUnpaidGiftCertificates]);

  useEffect(() => {
    if (!manualPriceEdit) {
      setTotalPrice(calculateTotalPrice());
    }
  }, [selectedServiceIds, salesTemplate.amount, visitFee]);

  useEffect(() => {
    if (currentLocation?.address) {
      const address = currentLocation.address;
      if (address.contactInfo?.addressLine1 && address.contactInfo.zip) {
        setAddresses((prev) => {
          const updated = prev.filter((a) => a.type !== AddressDtoTypeEnum.Praxis);
          return [...updated, address];
        });
      }
    }
  }, [user]);

  useEffect(() => {
    const selectedTherapies = saleServices.filter((service) =>
      selectedServiceIds.includes(service.id!),
    );

    if (!user) {
      setGiftCertificatesPayload({ value: 0 });
      return;
    }

    const items = selectedTherapies.map((service) => ({
      amount: salesTemplate.amount || undefined,
      therapy: {
        id: service.id,
        abbreviation: service.abbreviation,
      },
    }));

    const text = items
      .map((item) =>
        item.amount ? `${item.therapy.abbreviation} ${item.amount}` : item.therapy.abbreviation,
      )
      .join(" ");

    const giftCertificate: GiftCertificateDto = {
      items,
      text,
      value: totalPrice,
      account: currentLocation,
    };

    setGiftCertificatesPayload(giftCertificate);
  }, [saleServices, selectedServiceIds, salesTemplate.amount, user, totalPrice]);

  useEffect(() => {
    if (accessUrl) {
      window.open(accessUrl, "_blank");
    }
  }, [accessUrl]);

  const handleAmountChange = (e: any) => {
    setManualPriceEdit(false);
    const parsed = parseInt(e.target.value);
    setSalesTemplate({
      ...salesTemplate,
      // Keep NaN out of the payload — an empty field means "no amount yet",
      // which also keeps the confirm button disabled.
      amount: Number.isNaN(parsed) ? undefined : Math.max(1, parsed),
    });
  };

  const handleAddressChange = (e: any) => {
    setSalesTemplate({
      ...salesTemplate,
      addressId: e.target.value,
    });
  };

  const handleSelectService = (id: string) => {
    setSelectedServiceIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id],
    );
    setManualPriceEdit(false);
  };

  const handleTotalPriceChange = (e: any) => {
    setManualPriceEdit(true);
    // Clearing the field yields "" -> parseFloat gives NaN, which serialises to
    // JSON null and hits the NOT NULL constraint on gift_certificate.value.
    const parsed = parseFloat(e.target.value);
    setTotalPrice(Number.isNaN(parsed) ? 0 : parsed);
  };

  const handleResetPrice = () => {
    setManualPriceEdit(false);
    setTotalPrice(calculateTotalPrice());
  };

  const handleConfirm = async () => {
    try {
      setLoadingSave(true);
      const response = await createGiftCertificate(giftCertificatesPayload);
      if (response) {
        setGCDisplay(response);
      }
      setSelectedServiceIds([]);
      setSalesTemplate({ therapies: [], amount: undefined, addressId: "" });
      setTotalPrice(0);
      setVisitFee(undefined);
      setDataRefreshFlag(!dataRefreshFlag);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message;
      notificationStore.showMessage(
        `Fehler beim Erstellen des Gutscheins: ${errorMessage}`,
        "error",
      );
    } finally {
      setLoadingSave(false);
    }
  };
  const columns = [
    {
      label: "Heilmittel",
      render: (s: any) => s.abbreviation,
    },
    {
      label: "Beschreibung",
      render: (s: any) => s.description || "-",
    },
    {
      label: "Preis (€)",
      render: (s: any) => `${s.price} €`,
    },
  ];

  const visitFeeOptions = [
    { label: "HB", value: "HB" },
    { label: "keine", value: "keine" },
  ];

  return (
    <Grid container spacing={3} sx={{ overflowY: "auto", height: "calc(100vh - 80px)" }}>
      {/* First Card: Gift Certificate Creation */}
      <Grid item xs={12} md={5}>
        <Card sx={{ borderRadius: 3, height: "100%", display: "flex", flexDirection: "column" }}>
          <CardHeader title="Gutschein erstellen" />
          <CardContent sx={{ padding: 2, flex: 1, display: "flex", flexDirection: "column" }}>
            <Typography textAlign="left">
              Um den Gutscheinwert zu berechnen, wähle zuerst die gewünschten Leistungen aus.
            </Typography>
            {/* Table Section */}
            <FlatCard title={undefined}>
              <SelectableTable
                columns={columns}
                data={saleServices}
                isLoading={loading}
                onRowClick={(service) => handleSelectService(service.id!)}
                isSelected={(service) => selectedServiceIds.includes(service.id!)}
                emptyMessage="Keine Leistungen gefunden."
                rowHeight={60}
              />
            </FlatCard>
            <Typography textAlign="left">
              Zunächst wähle Haus- oder Praxisbesuch und bestimmte die Anzahl der Termine.
            </Typography>
            {/* Bottom-Right Summary Box */}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Box
                sx={{
                  width: 300,
                  p: 3,
                  backgroundColor: "grey.100",
                  borderRadius: 2,
                }}
              >
                <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                  <Controls.Select
                    name="visitFee"
                    label="Pauschale"
                    value={visitFee || "keine"}
                    onChange={(e: any) => {
                      const selectedValue = e.target.value === "keine" ? null : e.target.value;
                      setVisitFee(selectedValue);
                    }}
                    options={visitFeeOptions}
                    style={{
                      width: "100%",
                      justifyContent: "space-between",
                      height: "unset",
                    }}
                  />
                  {(selectedAddressType === AddressDtoTypeEnum.Haus ||
                    selectedAddressType === AddressDtoTypeEnum.Heim) && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body1">Pauschale-{visitFee}</Typography>
                    </Box>
                  )}
                </FormControl>

                <TextField
                  type="number"
                  label="Menge"
                  variant="outlined"
                  fullWidth
                  value={salesTemplate.amount ?? ""}
                  onChange={handleAmountChange}
                  sx={{ mb: 2 }}
                  required
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  type="number"
                  label="Gesamtbetrag"
                  variant="outlined"
                  fullWidth
                  value={totalPrice}
                  onChange={handleTotalPriceChange}
                  sx={{ mb: 2 }}
                  InputLabelProps={{ shrink: true }}
                />

                <Button
                  variant="contained"
                  onClick={handleConfirm}
                  disabled={!salesTemplate.amount}
                >
                  {loadingSave ? (
                    <CircularProgress size={24} sx={{ color: "black" }} />
                  ) : (
                    "Gutschein erstellen"
                  )}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {gcDisplay && (
        <Dialog open={true} onClose={() => setGCDisplay(null)}>
          <DialogTitle>Gutschein erstellt</DialogTitle>
          <DialogContent>
            <Typography variant="h6">
              <strong>Code:</strong> {gcDisplay.codeLowSecurity || "Nicht verfügbar"}
            </Typography>
            <Typography variant="h6">
              <strong>Wert:</strong> €{gcDisplay.value?.toFixed(2)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGCDisplay(null)} variant="text">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Grid item xs={7}>
        <Card>
          <CardHeader title="Unbezahlte Geschenkgutscheine" />
          <CardContent>
            <GCTable dataRefreshFlag={dataRefreshFlag} setDataRefreshFlag={setDataRefreshFlag} />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
});

export default SalesPageMain;
